import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCategoryStore } from '@/stores/categoryStore';
import { cn } from '@/lib/utils';
import { Star, Loader2 } from 'lucide-react';

// Map icon names to emoji or unicode characters for web
const iconMap: Record<string, string> = {
  'select_all': '📋',
  'auto_awesome': '✨',
  'palette': '🎨',
  'hearing': '👂',
  'face_retouching_natural': '💄',
  'menu_book': '📚',
  'work': '💼',
  'directions_car': '🚗',
  'star': '⭐',
  'sentiment_very_satisfied': '😄',
  'content_cut': '✂️',
  'currency_bitcoin': '₿',
  'today': '📅',
  'nightlife': '💃',
  'school': '🎓',
  'theater_comedy': '🎭',
  'emoji_events': '🏆',
  'checkroom': '👔',
  'account_balance': '🏦',
  'fitness_center': '💪',
  'restaurant': '🍽️',
  'smartphone': '📱',
  'sports_esports': '🎮',
  'yard': '🌱',
  'health_and_safety': '🏥',
  'history_edu': '📜',
  'home': '🏠',
  'translate': '🌐',
  'tag_faces': '😎',
  'psychology': '🧠',
  'two_wheeler': '🏍️',
  'trending_up': '📈',
  'movie': '🎬',
  'music_note': '🎵',
  'forest': '🌲',
  'newspaper': '📰',
  'record_voice_over': '🗣️',
  'more_horiz': '⋯',
  'hiking': '🥾',
  'visibility': '👁️',
  'family_restroom': '👨‍👩‍👧',
  'pets': '🐾',
  'photo_camera': '📷',
  'how_to_vote': '🗳️',
  'code': '💻',
  'help': '❓',
  'favorite': '❤️',
  'science': '🔬',
  'self_improvement': '🧘',
  'sports_soccer': '⚽',
  'auto_stories': '📖',
  'devices': '🖥️',
  'flight': '✈️',
  'policy': '🔍',
  'public': '🌍',
  'edit_note': '✍️',
  '18_up_rating': '🔞',
};

export function CategoriesPage() {
  const {
    categories,
    categoriesLoading,
    categoriesError,
    loadCategories,
    starCategory,
    unstarCategory,
  } = useCategoryStore();

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const starredCategories = categories.filter((c) => c.isStarred);
  const allCategories = categories;

  const handleStarToggle = (e: React.MouseEvent, categoryId: number, isStarred: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStarred) {
      unstarCategory(categoryId);
    } else {
      starCategory(categoryId);
    }
  };

  if (categoriesLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-7.5rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-7.5rem)] px-4">
        <p className="text-red-500 text-sm mb-4">{categoriesError}</p>
        <button
          onClick={() => loadCategories()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold text-card-foreground">Categories</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Starred categories section */}
        {starredCategories.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-foreground mb-3">Your Categories</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {starredCategories.map((category) => (
                <CategoryTile
                  key={category.id}
                  category={category}
                  onStarToggle={(e) => handleStarToggle(e, category.id, category.isStarred)}
                />
              ))}
            </div>
            <hr className="my-6 border-border" />
          </div>
        )}

        {/* All categories section */}
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-1">All Categories</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Tap the star to add categories to your feed
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {allCategories.map((category) => (
              <CategoryTile
                key={category.id}
                category={category}
                onStarToggle={(e) => handleStarToggle(e, category.id, category.isStarred)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface CategoryTileProps {
  category: {
    id: number;
    name: string;
    slug: string;
    icon: string;
    isStarred: boolean;
    isAdultOnly: boolean;
  };
  onStarToggle: (e: React.MouseEvent) => void;
}

function CategoryTile({ category, onStarToggle }: CategoryTileProps) {
  const icon = iconMap[category.icon] || '📁';

  return (
    <Link
      to={`/categories/${category.slug}`}
      className={cn(
        'relative flex flex-col items-center justify-center p-3 rounded-xl transition-colors aspect-square',
        category.isAdultOnly
          ? 'bg-red-500/10 hover:bg-red-500/20'
          : 'bg-muted hover:bg-muted/80'
      )}
    >
      {/* Star button */}
      <button
        onClick={onStarToggle}
        className="absolute top-1 right-1 p-1"
      >
        <Star
          className={cn(
            'w-4 h-4 transition-colors',
            category.isStarred
              ? 'fill-amber-400 text-amber-400'
              : 'text-muted-foreground/50 hover:text-muted-foreground'
          )}
        />
      </button>

      {/* Icon */}
      <span className="text-2xl mb-1" role="img" aria-label={category.name}>
        {icon}
      </span>

      {/* Name */}
      <span
        className={cn(
          'text-[10px] sm:text-xs font-medium text-center line-clamp-2',
          category.isAdultOnly ? 'text-red-500' : 'text-foreground'
        )}
      >
        {category.name}
      </span>
    </Link>
  );
}
